import type { MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import { Box, Flex, Text } from '@theme-ui/components';
import { css } from '@emotion/react';

import { useOnMount } from '@app/hooks/lifecycle';

import type {
  getMedia_media_chapters,
  getMedia_media_chapters_MP3Chapter as MP3Chapter,
} from '../../pages/ProductFinder/__generated__/getMedia';
import type { AudioPlayerState } from '../../redux/reducers/audioPlayer';
import { H4 } from '../typography';
import AE from './AudioEngine';
import styles from './AudioPlayer.styles';
import type { AudioPlayerDispatchers, PlayBackInfo } from './AudioPlayers.type';

export interface AudioPlayerProps
  extends AudioPlayerState,
    AudioPlayerDispatchers {
  playlist: getMedia_media_chapters[];
}

const AudioPlayer = (props: AudioPlayerProps) => {
  const [playBackInfo, setPlayBackInfo] = useState<PlayBackInfo | null>(null);
  const currentTrack = props.playlist[props.currentTrack];

  /**
   * Play the sound and dispatch the related redux action.
   */
  function playSound(): void {
    AE.play();
    props.play();
  }

  /**
   * Pause the sound engine and dispatch the related redux action.
   */
  function pauseSound(): void {
    AE.pause();
    props.pause();
  }

  /**
   * Define if sound needs to be played or paused.
   */
  function onPlayPause(): void {
    if (props.isPlaying && AE.buffer !== undefined) {
      pauseSound();
    } else if (AE.buffer === undefined) {
      props.load();
      AE.load(props.currentTrack).then(
        () => {
          props.open();
          playSound();
        },
        () => {
          // biome-ignore lint/suspicious/noConsole: We want this
          console.error('Unable to load the audio file.');

          /**
           * If some error occurs when trying to load the audio file,
           * unload the buffer from the Audio Engine.
           */
          AE.unloadBuffer();
          props.close();
          props.reset();
        },
      );
    } else {
      playSound();
    }
  }

  /**
   * Play the next sound in the playlist.
   */
  function onNextSound(): void {
    props.load();
    AE.next().then(
      () => {
        props.next();
        playSound();
      },
      () => {
        // biome-ignore lint/suspicious/noConsole: We want this
        console.error('Unable to play next sound');
      },
    );
  }

  /**
   * Play the previous sound in the playlist.
   */
  function onPrevSound(): void {
    props.load();
    AE.prev().then(
      () => {
        props.prev();
        playSound();
      },
      () => {
        // biome-ignore lint/suspicious/noConsole: We want this
        console.error('Unable to play previous sound');
      },
    );
  }

  /**
   * Close the AudioPlayer and unload the AudioEngine buffer.
   */
  const onClose = (): void => {
    AE.unloadBuffer();
    props.close();
  };

  const onSeek = (event: MouseEvent): void => {
    const target = event.target as HTMLDivElement;
    const bounds = target.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const percentagePos = (x / target.offsetWidth) * 100;
    AE.seek(percentagePos);
  };

  /**
   * On component mount subscribe to AudioEngine subjects.
   * On component will destroy unsubscribe from the AudioEngine subjects.
   */
  useOnMount(() => {
    AE.onTrackEnd.subscribe(onNextSound);
    AE.playBackInfo.subscribe((info) => setPlayBackInfo(info));

    return () => {
      AE.onTrackEnd.unsubscribe();
      AE.playBackInfo.unsubscribe();
      AE.unloadBuffer();
    };
  });

  useEffect(() => {
    if (props.data !== null) {
      AE.playlist = props.playlist
        .map((chapter) => (chapter && (chapter as MP3Chapter).mediaHigh) || '')
        .filter((audioTrack) => audioTrack !== '');
      onPlayPause();
    }
    // This will be deleted in ni-v2, no need to fix!
  }, [props.data, props.playlist]);

  return (
    <Box
      css={css`
        ${styles.outerWrapper};
        ${props.isOpen && styles.openPlayer}
      `}
    >
      {props.data !== null && currentTrack ? (
        <Box css={styles.wrapper}>
          <Flex css={styles.contentWrapper}>
            {/* CONTROLS */}
            <Box>
              <Flex>
                {/* Prev button */}
                <button css={styles.nispButton} onClick={onPrevSound}>
                  <span css={styles.prevButton} />
                </button>

                {/* Play/Pause button */}
                <button css={styles.nispButton} onClick={onPlayPause}>
                  <span
                    css={css`
                      ${
                        props.isPlaying ? styles.pauseButton : styles.playButton
                      }
                    `}
                  />
                  {props.isAudioLoading && <span css={styles.loading} />}
                </button>

                {/* Next button */}
                <button css={styles.nispButton} onClick={onNextSound}>
                  <span css={styles.nextButton} />
                </button>
              </Flex>
              <Text sx={{ display: 'block' }} css={styles.miniPlayTime}>
                {playBackInfo && playBackInfo.time}
              </Text>
            </Box>

            {/* TRACK META */}
            <Box mr={2} css={styles.metaWrapper}>
              <Flex
                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                mb={3}
              >
                <H4 css={styles.trackTitle}>
                  {(currentTrack as MP3Chapter).title}
                </H4>
                <Text sx={{ display: 'block', fontSize: 0 }}>
                  {props.currentTrack + 1}/{props.playlist.length}
                </Text>
              </Flex>

              <Box
                onClick={onSeek}
                css={css`
                  ${styles.waveForm};
                  background-image: url(${
                    (currentTrack as MP3Chapter).waveform
                  });
                `}
              >
                {playBackInfo != null && (
                  <Box
                    css={css`
                      ${styles.playhead};
                      left: ${playBackInfo && playBackInfo.position}%;
                    `}
                  >
                    <Box p={1} css={styles.transportBar}>
                      {playBackInfo && playBackInfo.time}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* TRACK DESCRIPTION */}
            <Box
              css={styles.descWrapper}
              sx={{ width: ['100%', '50%', '33.3%'] }}
            >
              <Text
                sx={{ fontSize: 0, display: 'block' }}
                dangerouslySetInnerHTML={{
                  __html: (currentTrack as MP3Chapter).description ?? '',
                }}
              />
            </Box>

            {/* Close button */}
            <button css={styles.nispButton} onClick={onClose}>
              <span css={styles.closeButton} />
            </button>
          </Flex>
        </Box>
      ) : null}
    </Box>
  );
};

export default AudioPlayer;
